import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { rowActionLabel } from '../../lib/deleteLabel'
import { useLocalizedText } from '../../lib/localizedText'
import { exportOperationCodes } from './api/operationCodeApi'
import { Dropdown } from 'primereact/dropdown'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import RefreshButton from '../../components/ui/RefreshButton'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import { useToast } from '../../components/ui/ToastProvider'
import { isOptimisticLock } from '../../api/apiError'
import { searchTerm } from '../../lib/searchTerm'
import { operationCodeColumns } from './components/OperationCodeColumns'
import OperationCodeFormDialog from './components/OperationCodeFormDialog'
import { useOperationCodes } from './hooks/useOperationCodes'
import type { OperationCode } from './types'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import { Paginator, type PaginatorPageChangeEvent } from 'primereact/paginator'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
import './admin.css'

/** Varsayılan sayfa boyutu (anayasa: tüm listelerde 10). */
const PAGE_SIZE = 10

/** Arama debounce süresi (anayasa Page Standards ~400 ms). */
const DEBOUNCE_MS = 400

/**
 * "Tümü" seçeneğinin değeri (FR-006: listenin başında "Tümü" bulunur). Boş-OLMAYAN bir sentinel
 * gerekir: PrimeReact Dropdown, `null`/`''` değerli seçeneği "boş seçim" sayıp `onChange`'i yutar
 * (aynı sorun `ReferansAdminPage`'de `ALL_TYPES` ile belgelenmiştir). Negatif değer seçildi çünkü
 * `REFERANS.ID` daima pozitiftir → gerçek bir modülle çakışamaz. API'ye giderken `undefined`'a
 * (filtresiz) çevrilir.
 */
const ALL_MODULES = -1

/**
 * Admin → **İşlem Kodu** (045). `REF_OPERATION_CODE` için **salt liste + TR/EN ad düzenleme**.
 *
 * <p>Bilinçli olarak **yok**: "Yeni ekle", "Sil", durum toggle'ı, durum filtresi ve kolon sıralaması
 * (spec FR-002a/FR-002b/FR-009). Kayıt kümesi ve modül ataması veritabanı tarafında yönetilir.
 *
 * <p>Filtreler: ad araması (TR **veya** EN, sunucu tarafında TR-farkında) + Modül combosu; ikisi VE
 * mantığıyla birleşir. Sayfalama **sunucu-taraflıdır** (`lazy`); filtre değişiminde sayfa sıfırlanır
 * (FR-007). Yetki: `OPERATION_CODE_MANAGE`.
 *
 * <p>Kısıtlı Yetkili asistan (126) ekranı <b>salt okunur</b> görür: Düzenle eylemi tabloda da kartta
 * da çizilmez.
 */
export default function OperationCodesAdminPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const toast = useToast()
  const { readOnly } = useProxyMode()

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [moduleId, setModuleId] = useState<number>(ALL_MODULES)
  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(PAGE_SIZE)

  // Arama debounce'u + sayfa sıfırlama (FR-005, FR-007).
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search)
      setFirst(0)
    }, DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [search])

  const { list, modules, update } = useOperationCodes({
    q: searchTerm(debounced),
    // "Tümü" → filtresiz (API'ye moduleId gönderilmez).
    moduleId: moduleId === ALL_MODULES ? null : moduleId,
    page: Math.floor(first / rows),
    size: rows,
  })

  const items = list.data?.content ?? []
  const total = list.data?.page.totalElements ?? 0
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const [editing, setEditing] = useState<OperationCode | null>(null)

  // "Tümü" listenin BAŞINDA, istemcide üretilir (FR-006); modül kimlikleri koda gömülmez.
  const moduleOptions = useMemo(
    () => [
      { label: t('admin.operationCodes.filter.moduleAll'), value: ALL_MODULES },
      ...(modules.data ?? []).map((m) => ({ label: m.name, value: m.id })),
    ],
    [modules.data, t],
  )

  const hasFilter = search.trim().length > 0 || moduleId !== ALL_MODULES
  const clearAll = () => {
    setSearch('')
    setModuleId(ALL_MODULES)
    setFirst(0)
  }

  const onSubmit = (nameTr: string, nameEn: string | null) => {
    if (!editing) return
    update.mutate(
      { id: editing.id, body: { nameTr, nameEn, modDate: editing.modDate } },
      {
        onSuccess: () => {
          toast.show({
            severity: 'success',
            summary: t('admin.operationCodes.toast.updated'),
            life: 3000,
          })
          setEditing(null)
        },
        onError: (err) => {
          // Optimistic-lock/generic toast'ı merkezî hata yönetimi (019) basar; ekran yalnız tazeler.
          if (isOptimisticLock(err)) {
            setEditing(null)
            list.refetch()
          }
        },
      },
    )
  }

  return (
    <div className="admin-page">
      <OperationCodeFormDialog
        visible={editing != null}
        initial={editing}
        submitting={update.isPending}
        onSubmit={onSubmit}
        onHide={() => setEditing(null)}
      />

      <PageHeader
        title={t('nav.admin.operationCodes')}
        actions={
          <>
            {/* Yenile, Excel'in SOLUNDA — Figma tablo tasarimi (kullanici istegi 2026-08-26).
                Onceden filtre cubugundaydi (`FilterBar onRefresh`). */}
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton onExport={() => exportOperationCodes(debounced, moduleId === ALL_MODULES ? undefined : moduleId)} disabled={items.length === 0} />
          </>
        }
      />

      <FilterBar
        searchValue={search}
        searchPlaceholder={t('admin.operationCodes.search')}
        searchAriaLabel={t('admin.operationCodes.search')}
        onSearch={setSearch}
        showClear={hasFilter}
        clearLabel={t('admin.operationCodes.filter.clear')}
        onClear={clearAll}
      >
        {/*
          "Tümü" açık bir seçenek olduğundan `showClear` (X) kullanılmaz — aynı işi yapan iki kontrol
          olmaz ve seçim hiçbir zaman boşa düşmez. aria-label seçenek metninden AYRIDIR: kontrolün ne
          olduğunu söyler ("Modül filtresi"), seçili değer ise "Tümü"/modül adıdır.
        */}
        <Dropdown
          value={moduleId}
          options={moduleOptions}
          onChange={(e) => {
            setModuleId((e.value as number | null) ?? ALL_MODULES)
            setFirst(0)
          }}
          aria-label={t('admin.operationCodes.filter.moduleLabel')}
          disabled={modules.isError}
          // Panel yüksekliği: varsayılan 200px (≈4-5 seçenek) yerine 10 seçenek görünsün. Seçenek
          // ≈ 2×0.75rem dolgu + ~1.05rem satır ≈ 2.6rem; liste kendi 0.75rem üst/alt dolgusunu
          // ekler → 10 × 2.6 + 1.5 ≈ 27.1rem, 28rem güvenli taban.
          scrollHeight="28rem"
        />
      </FilterBar>

      {/* Sunucu-taraflı sayfalama (lazy). Kolon sıralaması kapalı → sortField/onSort verilmez. */}
      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && items.length === 0 && (
            <EmptyState
              title={t('admin.operationCodes.empty.title')}
              description={t('admin.operationCodes.empty.desc')}
            />
          )}
          {!list.isLoading &&
            items.map((row) => {
              const active = row.recordStatus === 1
              return (
                <div key={row.id} className="ui-card">
                  <div className="ui-card-top">
                    <Badge tone={active ? 'green' : 'neutral'}>
                      {active
                        ? t('admin.operationCodes.status.active')
                        : t('admin.operationCodes.status.passive')}
                    </Badge>
                    {!readOnly && (
                      <div className="ui-card-actions ui-card-actions--compact">
                        <Button
                          icon="pi pi-pencil"
                          rounded
                          text
                          /* Erişilebilir ad KAYDI da söyler (THY AI review, 26.10.2026 — ikinci tur). Projenin kendi
   yardımcısı `rowActionLabel` kullanılır: ad boşsa adsız anahtara düşer. */
                          aria-label={rowActionLabel(t, 'admin.operationCodes.actions.edit', localized(row.nameTr, row.nameEn))}
                          onClick={() => setEditing(row)}
                        />
                      </div>
                    )}
                  </div>
                  <div className="ui-card-head">
                    <span className="ui-card-title">{localized(row.nameTr, row.nameEn)}</span>
                  </div>
                  {row.moduleName && <span className="ui-card-row">{row.moduleName}</span>}
                </div>
              )
            })}
          {!list.isLoading && total > rows && (
            <Paginator
              first={first}
              rows={rows}
              totalRecords={total}
              onPageChange={(e: PaginatorPageChangeEvent) => {
                setFirst(e.first)
                setRows(e.rows)
              }}
              template="PrevPageLink CurrentPageReport NextPageLink"
              currentPageReportTemplate={t('common.pageReport')}
              className="ui-cards-paginator"
            />
          )}
        </div>
      ) : (
      <DataTable
        value={items}
        loading={list.isLoading}
        lazy
        first={first}
        rows={rows}
        totalRecords={total}
        onPage={(e) => {
          setFirst(e.first)
          setRows(e.rows)
        }}
        emptyMessage={
          <EmptyState
            title={t('admin.operationCodes.empty.title')}
            description={t('admin.operationCodes.empty.desc')}
          />
        }
      >
        {operationCodeColumns({ t, localized, onEdit: setEditing, readOnly })}
      </DataTable>
      )}
    </div>
  )
}
